import { format } from "date-fns";
import { CommandXFooter } from "./CommandXFooter";

const r2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(r2(n));

const fmtQty = (n: number) =>
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(r2(n));

interface BillLineItem {
  description: string;
  quantity: number;
  unit_price: number;
  line_total: number;
  sov_line_description?: string;
}

interface BillPrintData {
  bill_number: string;
  bill_date: string;
  due_date?: string | null;
  status: string;
  subtotal: number;
  retainage_amount: number;
  retainage_percent: number;
  total: number;
  vendor_name?: string;
  project_name?: string;
  project_number?: string;
  company_name?: string;
  company_logo_url?: string | null;
  company_address?: string | null;
  company_city?: string | null;
  company_state?: string | null;
  company_zip?: string | null;
  description?: string | null;
  payment_terms?: string | null;
  line_items?: BillLineItem[];
}

export const BillPrintTemplate = ({ data }: { data: BillPrintData }) => (
  <div className="print-template p-8 max-w-[8.5in] mx-auto font-sans text-sm leading-relaxed">
    {/* Header */}
    <div className="flex justify-between items-start border-b pb-4 mb-8">
      <div>
        {data.company_logo_url && (
          <img src={data.company_logo_url} alt={data.company_name ?? "Company"} style={{ maxHeight: "40px", objectFit: "contain", marginBottom: "4px" }} />
        )}
        <h1 className="text-2xl font-bold tracking-tight" style={{ color: "#111" }}>BILL</h1>
        {data.company_name && (
          <p className="text-base font-medium mt-1" style={{ color: "#333" }}>{data.company_name}</p>
        )}
        {(data.company_address || data.company_city) && (
          <p className="text-xs mt-0.5" style={{ color: "#666" }}>
            {data.company_address && <>{data.company_address}<br /></>}
            {[data.company_city, data.company_state].filter(Boolean).join(", ")}
            {data.company_zip ? ` ${data.company_zip}` : ""}
          </p>
        )}
      </div>
      <div className="text-right">
        <p className="text-lg font-bold font-mono" style={{ color: "#111" }}>{data.bill_number}</p>
        <p style={{ color: "#666" }}>Date: {format(new Date(data.bill_date), "MMMM d, yyyy")}</p>
        {data.due_date && (
          <p style={{ color: "#666" }}>Due: {format(new Date(data.due_date), "MMMM d, yyyy")}</p>
        )}
        <p className="mt-1 inline-block px-2 py-0.5 rounded text-xs font-semibold uppercase" style={{ background: "#f3f4f6", color: "#374151" }}>
          {data.status}
        </p>
      </div>
    </div>

    {/* Vendor / Project */}
    <div className="grid grid-cols-2 gap-8 mb-8">
      <div>
        <p className="text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: "#999" }}>Vendor</p>
        <p className="font-medium" style={{ color: "#111" }}>{data.vendor_name || "—"}</p>
        {data.payment_terms && (
          <p className="text-xs capitalize" style={{ color: "#666" }}>Terms: {data.payment_terms.replace("_", " ")}</p>
        )}
      </div>
      <div>
        <p className="text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: "#999" }}>Project</p>
        <p className="font-medium" style={{ color: "#111" }}>
          {data.project_name || "—"}
          {data.project_number && <span className="text-xs ml-1" style={{ color: "#666" }}>({data.project_number})</span>}
        </p>
      </div>
    </div>

    {data.description && (
      <div className="mb-6">
        <p className="text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: "#999" }}>Description</p>
        <p>{data.description}</p>
      </div>
    )}

    {/* Line Items */}
    {data.line_items && data.line_items.length > 0 && (
      <table className="w-full mb-6" style={{ borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ borderBottom: "2px solid #ddd" }}>
            <th className="text-left py-2.5 pl-1 text-xs uppercase tracking-wider" style={{ color: "#666" }}>Description</th>
            <th className="text-right py-2.5 text-xs uppercase tracking-wider" style={{ color: "#666" }}>Qty</th>
            <th className="text-right py-2.5 text-xs uppercase tracking-wider" style={{ color: "#666" }}>Unit Price</th>
            <th className="text-right py-2.5 text-xs uppercase tracking-wider" style={{ color: "#666" }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {data.line_items.map((li, i) => (
            <tr key={i} style={{ borderBottom: "1px solid #eee" }}>
              <td className="py-2.5 pl-1">
                {(() => {
                  const parts = li.description.split(" · ");
                  const title = parts[0];
                  const units = parts.slice(1).join(" · ");
                  return (
                    <>
                      <span className="font-medium">{title}</span>
                      {units && (
                        <span className="block text-xs mt-0.5" style={{ color: "#888" }}>{units}</span>
                      )}
                    </>
                  );
                })()}
              </td>
              <td className="text-right py-2.5">{fmtQty(li.quantity)}</td>
              <td className="text-right py-2.5">{fmt(li.unit_price)}</td>
              <td className="text-right py-2.5 font-medium">{fmt(li.line_total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}

    {/* Totals */}
    <div className="flex justify-end mt-4 pt-4" style={{ borderTop: "1px solid #eee" }}>
      <div className="w-64">
        <div className="flex justify-between py-1" style={{ borderBottom: "1px solid #eee" }}>
          <span style={{ color: "#666" }}>Subtotal</span>
          <span className="font-semibold">{fmt(data.subtotal)}</span>
        </div>
        <div className="flex justify-between py-1" style={{ borderBottom: "1px solid #eee" }}>
          <span style={{ color: "#666" }}>Retainage ({data.retainage_percent}%)</span>
          <span className="font-semibold" style={{ color: "#dc2626" }}>({fmt(data.retainage_amount)})</span>
        </div>
        <div className="flex justify-between py-2 text-base font-bold" style={{ borderTop: "2px solid #111" }}>
          <span>Total</span>
          <span>{fmt(data.total)}</span>
        </div>
      </div>
    </div>

      <CommandXFooter />
  </div>
);
